import React, { useContext, useEffect, useMemo, useState } from "react";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Save, ArrowLeft } from "lucide-react";

const API = "https://nissi-india.com/api/companyMaster";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const CompanyBankAdd = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [params] = useSearchParams();

  // ASPX: company always 1
  const companyId = 1;
  const detailId = Number(params.get("bankId") || 0);
  const isEdit = detailId > 0;

  const [loading, setLoading] = useState(true);
  const [accountTypes, setAccountTypes] = useState([]);

  const [form, setForm] = useState({
    bankName: "",
    branch: "",
    ifscCode: "",
    accountTypeId: "",
    accountNo: "",
    holderName: "",
    remarks: "",
  });

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pageTitle = useMemo(
    () => (isEdit ? "COMPANY BANK UPDATE" : "COMPANY BANK ADD"),
    [isEdit]
  );

  const loadAccountTypes = async () => {
    const r = await axios.get(`${API}/account-types`, {
      headers: tenantHeaders(),
      params: tenantParams(),
    });
    setAccountTypes(r.data.data || []);
  };

  const loadBankDetail = async () => {
    if (!isEdit) return;
    const r = await axios.get(`${API}/company/${companyId}/bank/${detailId}`, {
      headers: tenantHeaders(),
      params: tenantParams(),
    });    const d = r.data.data;

    setForm({
      bankName: d.fld_bankname || "",
      branch: d.fld_bankbranch || "",
      ifscCode: d.fld_ifsccode || "",
      accountTypeId: String(d.fld_accounttypeid || ""),
      accountNo: d.fld_accountnumber || "",
      holderName: d.fld_holdername || "",
      remarks: d.remarks || "",
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadAccountTypes();
        await loadBankDetail();
      } catch (e) {
        console.error(e);
        toast.error("Failed to load bank details");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId]);

  const validateForm = () => {
    if (!form.bankName.trim()) return toast.error("Please enter Bank name"), false;
    if (!form.branch.trim()) return toast.error("Please enter Branch"), false;
    if (!form.accountTypeId) return toast.error("Please select Account Type"), false;
    if (!form.accountNo.trim()) return toast.error("Please enter Account Number"), false;
    return true;
  };

  const onSave = async () => {
    try {
      if (!validateForm()) return;

      const payload = {
        isUpdate: isEdit,
        detailId,
        bankName: form.bankName,
        branch: form.branch,
        ifscCode: form.ifscCode,
        accountTypeId: Number(form.accountTypeId),
        accountNo: form.accountNo,
        holderName: form.holderName,
        remarks: form.remarks,
      };

      const r = await axios.post(`${API}/company/${companyId}/bank/save`, payload, {
        headers: tenantHeaders(),
      });
      if (r.data.ok) {
        toast.success(isEdit ? "Updated Successfully" : "Inserted Successfully");
        // go back to CompanyMaster bank tab
        navigate("/adminMaster/companyMaster?tab=bank");
      } else {
        toast.error("Save failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "Save failed");
    }
  };

  return (
    <AdminLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{pageTitle}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top actions */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              onClick={() => navigate("/adminMaster/companyMaster?tab=bank")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
                isDark
                  ? "border-white/10 bg-white/5 hover:bg-white/10 text-white"
                  : "border-gray-200 bg-gray-50 hover:bg-white text-gray-900"
              } transition`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <button
              onClick={onSave}
              disabled={loading}
              className={`
                flex items-center gap-2
                px-5 py-2 rounded-xl
                text-white font-semibold
                shadow-lg transition-all duration-200
                ${buttonClass} ${hoverClass}
                ${loading ? "opacity-60 cursor-not-allowed" : "hover:-translate-y-0.5"}
              `}
            >
              <Save className="w-4 h-4" />
              {isEdit ? "Update" : "Submit"}
            </button>
          </div>

          {/* Form */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Bank Name *</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.bankName}
                onChange={(e) => setField("bankName", e.target.value)}
                placeholder="Enter bank name"
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Branch *</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.branch}
                onChange={(e) => setField("branch", e.target.value)}
                placeholder="Enter branch"
              />
            </div>

            <div>
              <label className="text-sm font-semibold">IFSC Code</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.ifscCode}
                onChange={(e) => setField("ifscCode", e.target.value)}
                placeholder="IFSC"
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Account Type *</label>
              <select
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.accountTypeId}
                onChange={(e) => setField("accountTypeId", e.target.value)}
              >
                <option value="">Select</option>
                {accountTypes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold">Account Number *</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.accountNo}
                onChange={(e) => setField("accountNo", e.target.value)}
                placeholder="Account Number"
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Holder Name</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.holderName}
                onChange={(e) => setField("holderName", e.target.value)}
                placeholder="Holder name"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Remarks</label>
              <textarea
                className="w-full mt-1 p-2 rounded-lg border"
                rows={3}
                value={form.remarks}
                onChange={(e) => setField("remarks", e.target.value)}
                placeholder="Remarks"
              />
            </div>
          </div>
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default CompanyBankAdd;
